import { Centered, EmptyState, FlexColumn, MaterialIcon, useStyles } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { LoadingSpinner } from 'baseui/button/styled-components';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { environment } from '../../../../environments/environment';
import { getDeviceConnectionStatus, postStreamingCommand } from '../../../../services/streamingService';
import { apiParamsState, isAllWorkspacesSelect } from '../../../../storage/app';
import { streamingCurrentStateState, streamingState } from '../../../../storage/streaming';
import { RightCard } from '../../../../types/app';
import { MinimalApiAsset } from '../../../../types/asset';
import { ChannelId } from '../../../../types/gptTypes';
import { StreamingCommand } from '../../../../types/streaming';
import FloatingRightCard from '../../../layout/cards/FloatingRightCard';
import StreamingWindow from './StreamingWindow';

function FloatingStreamingWindow() {
  const { theme, css } = useStyles();
  const streaming = useRecoilValue(streamingState);
  const [connected, setConnected] = useState<boolean>();
  const currentState = useRecoilValue(streamingCurrentStateState);
  const channelsCount = currentState?.asset.channels;
  const asset: MinimalApiAsset | undefined =
    currentState?.asset?.subClient && currentState?.asset.client
      ? {
          assetId: currentState.asset._id,
          clientId: currentState.asset.client._id,
          subClientId: currentState.asset.subClient._id,
        }
      : undefined;
  const initialScale = channelsCount === 3 ? 1.2 : 1.8;
  const rows = channelsCount === 3 ? 3 : 2;
  const [reload, setReload] = useState(0);
  const [posters, setPosters] = useState({
    [ChannelId.front]: '',
    [ChannelId.internal]: '',
    [ChannelId.dome]: '',
  });
  const apiParams = useRecoilValue(apiParamsState);
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);

  useEffect(() => {
    setConnected(undefined);
    const validateConnection = async () => {
      if (!asset) {
        setConnected(false);
        return;
      }
      const { connected, error } = await getDeviceConnectionStatus({
        assetId: asset.assetId,
        clientId: asset.clientId,
        subClientId: asset.subClientId,
      });
      if (error) {
        setConnected(false);
      } else {
        setConnected(connected);
      }
    };
    validateConnection();
  }, [asset?.assetId, asset?.clientId, asset?.subClientId, reload]);

  useEffect(() => {
    if (!connected || environment.withLivePosters.toLowerCase() !== 'enabled') {
      return;
    }
    setPosters({
      [ChannelId.front]: '',
      [ChannelId.internal]: '',
      [ChannelId.dome]: '',
    });
    const loadPosters = async () => {
      if (!asset) return;
      let channels = [ChannelId.front, ChannelId.internal];
      if (channelsCount === 3) channels.push(ChannelId.dome);
      const requests = channels.map((channel) =>
        postStreamingCommand({
          assetId: asset.assetId,
          command: StreamingCommand.takePhoto,
          channelId: channel,
          clientId: apiParams?.clientId,
          subClientId: isAllWorkspacesSelected ? undefined : apiParams?.subClientId,
        }),
      );
      const results = await Promise.allSettled(requests);
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const response = result.value.streaming?.flespiResponse.result[0]?.response;
          const url = response?.url;
          const channel = response?.meta?.channel;
          if (url && channel) {
            setPosters((prev) => ({ ...prev, [channel]: url }));
          }
        }
      });
    };
    loadPosters();
  }, [asset?.clientId, asset?.subClientId, asset?.assetId, connected, channelsCount]);

  if (!streaming.ident || !asset || !channelsCount) {
    return null;
  }

  return (
    <FloatingRightCard
      name={RightCard.streaming}
      initialScale={initialScale}
      rows={rows}
    >
      <FlexColumn classNames={css({ height: '100%' })}>
        {connected === undefined && (
          <Centered>
            <LoadingSpinner $size="large" />
          </Centered>
        )}
        {connected && <StreamingWindow posters={posters} />}
        {connected === false && (
          <EmptyState
            title="Dispositivo desconectado ó iniciando"
            description="Podría tardar algunos minutos en establecer conexión."
            action={
              <Button
                shape="default"
                kind="secondary"
                size="mini"
                onClick={() => setReload((prev) => prev + 1)}
                startEnhancer={
                  <MaterialIcon
                    name="refresh"
                    size={theme.sizing.scale600}
                  />
                }
              >
                Reintentar
              </Button>
            }
          />
        )}
      </FlexColumn>
    </FloatingRightCard>
  );
}

export default FloatingStreamingWindow;
